import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { deleteClients, setClientsPaginated, updateClientInList } from "../../../redux/features/clientStateSlice";
import { deleteClientApi, invalidateContactsAndClients } from "../../../utils";
import { getClientByUidApi, getClients } from "../actions/getClients.actions";
import { updateClients as updateClientsApi } from "../actions/updateClients.actions";

interface Client {
  uid: string;
  firstName?: string;
  clientCompanyName?: string;
  clientWorkTypeId?: number;
  coverageAreas?: any[];
  type?: string;
  [key: string]: any;
}

interface ClientsResponse {
  items: Client[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface GetClientsParams {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderField?: string;
  search?: string;
  filterBy?: string;
  status?: string;
}

interface EditClientPayload {
  clientCompanyName?: string | null;
  clientWorkTypeId?: number;
  [key: string]: any;
}

export interface LinkContactResult {
  ok: boolean;
  data?: any;
  message?: string;
}

export const useClients = () => {
  const dispatch = useDispatch();
  const token = sessionStorage.getItem("token");

  const { companies } = useSelector((state: any) => state.userState);
  const clientState = useSelector((state: any) => state.clientState);
  const queryClient = useQueryClient();
  const clients = clientState.clients;

  const { showSnackbar } = useGlobalSnackbar();

  const companyId: number | undefined = companies?.[0]?.id;

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getAllClients = async ({ page = 1, pageSize = 10, orderBy, orderField, search, filterBy, status }: GetClientsParams) => {
    setLoading(true);
    setError(null);

    try {
      const data = await getClients({
        page,
        pageSize,
        orderBy,
        orderField,
        search,
        filterBy,
        token,
        status,
      });

      dispatch(setClientsPaginated(data));
      return { data, ok: true };
    } catch (err: any) {
      setError(err?.message || "Error fetching clients");
      return { data: null, ok: false };
    } finally {
      setLoading(false);
    }
  };

  const getClientByUid = useCallback(
    async (uid: string) => {
      setLoading(true);
      setError(null);

      try {
        const data = await getClientByUidApi(uid);

        if (data) {
          dispatch(updateClientInList(data));
        }

        return { data, ok: true };
      } catch (err: any) {
        setError(err?.message || "Error fetching client");
        return { data: null, ok: false };
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  const editClient = async (id: string, clientData: EditClientPayload) => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        clientUid: id,
        ...clientData,
        clientCompanyName: clientData.clientWorkTypeId === 2 ? null : clientData.clientCompanyName,
      };

      const { data } = await updateClientsApi(token, payload);

      // The API might return the updated client or just success.
      // If it returns the updated object, we use it. Otherwise we merge local data.
      const updatedItem = { uid: id, ...clientData, ...(data || {}) };
      dispatch(updateClientInList(updatedItem));
      return { data, ok: true };
    } catch (err: any) {
      setError(err.message || "Error updating client");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editClientCoverageAreas = async (id: string, updatedCoverage: any[]) => {
    setLoading(true);
    setError(null);

    const ALLOWED_COVERAGE_KEYS = [
      "id",
      "address",
      "city",
      "state",
      "country",
      "zipCode",
      "zip5",
      "zipPlus4",
      "zipSource",
      "latitude",
      "longitude",
      "milesOfCoverageArea",
      "name",
      "color",
    ];

    try {
      const cleanedCoverage = (updatedCoverage || []).map((area: any) => {
        if (!area || typeof area !== "object") return area;
        const cleaned: any = {};
        ALLOWED_COVERAGE_KEYS.forEach((key) => {
          let val = area[key];
          if (key === "milesOfCoverageArea" && val === undefined && area.radius !== undefined) {
            val = area.radius;
          }
          if (key === "zip5" || key === "zipPlus4" || key === "zipSource" || (val !== null && val !== undefined)) {
            cleaned[key] = val ?? null;
          }
        });
        return cleaned;
      });

      const payload = {
        clientUid: id,
        coverageAreas: cleanedCoverage,
      };

      const { data } = await updateClientsApi(token, payload);

      dispatch(
        updateClientInList({
          uid: id,
          coverageAreas: data?.coverageAreas || updatedCoverage,
        }),
      );
      return { data, ok: true };
    } catch (err: any) {
      setError(err.message || "Error updating coverage areas");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeClient = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await deleteClientApi(token, id);
      await invalidateContactsAndClients(queryClient);
      dispatch(deleteClients(id));
    } catch (err: any) {
      setError(err.message || "Error deleting client");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const linkContact = async (client: any, contact: any): Promise<LinkContactResult> => {
    if (!contact || !contact.phoneNumbers || contact.phoneNumbers.length === 0) {
      throw new Error("This contact has no phone numbers to link");
    }

    const contactPhones = contact.phoneNumbers || [];
    const newPhonesToAdd = contactPhones
      .map((cp: any) => {
        const digits = cp.number.replace(/\D/g, "");
        let dialCode = "+1";
        let phone = digits;
        if (digits.startsWith("1") && digits.length === 11) {
          phone = digits.substring(1);
        }
        return {
          countryCode: "US",
          dialCode,
          phone,
          phoneNumber: dialCode + phone,
          tag: "other",
        };
      })
      .filter((p: any) => p.phone);

    const existingPhones = client.phoneNumbers || [];
    const filteredNewPhones = newPhonesToAdd.filter((newP: any) => !existingPhones.some((existP: any) => existP.phone === newP.phone));

    if (filteredNewPhones.length === 0) {
      return { ok: true, message: "This phone number is already linked to this client." };
    }

    const updatedPhones = [...existingPhones, ...filteredNewPhones];

    return await editClient(client.uid, {
      phoneNumbers: updatedPhones,
    });
  };

  return {
    clients,
    loading,
    error,
    getAllClients,
    removeClient,
    editClient,
    editClientCoverageAreas,
    getClientByUid,
    linkContact,
  };
};
